import {useEffect, useState} from 'react';

import {useNavigate, useParams} from 'react-router-dom';

import {Grid, Stack} from '@mantine/core';

import {RecordForm} from '../forms/RecordForm';

import {
    FormPageLayout,
    PlatformLoader,
    SimplePageContentWrapper,
    TabbedComponent,
} from '@flexinet/ui-components';
import {Profile} from '../profile/Profile';

import {EditPageNavigation} from '../navigation/EditPageNavigation';
import {useApplication} from '../hooks/useApplication.ts';
import {useApplicationsSelection} from '../hooks/useApplicationsSelection.ts';
import {useEditPageTools} from '../hooks/useEditPageTools';
import {rbacActions} from "../../../config/rbac/actions.ts";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {IndexPage as ApplicationsAccountsPage} from "../../application-accounts/pages/IndexPage.tsx";
import {IndexPage as ApplicationProductsPage} from "../../application-products/pages/IndexPage.tsx";
import {ApplicationProgressSection} from '../components/ApplicationProgressSection.tsx';
import {ApplicationInfrastructureSection} from '../components/ApplicationInfrastructureSection.tsx';
import {ProvisioningMetadataForm} from '../forms/ProvisioningMetadataForm';
import {ProvisionFormDrawer} from '../drawers/ProvisionFormDrawer';
import {stage} from '../../../config/stage';


export const EditPage = () => {

    const navigate = useNavigate();

    const {allowedActions} = useVerifiedPermissions()

    const {id: applicationId} = useParams();

    if (typeof applicationId === 'undefined') {
        throw new Error('applicationId is required parameter');
    }

    const {
        record: application,
        updateRecord,
        provisionRecord,
        provisioning,
        deprovisionRecord,
        markRecordDecommissioned,
    } = useApplication(applicationId);

    const {handleClearSelection} = useApplicationsSelection();

    // FNP-1013: the provision dialog. Local state rather than a route, matching
    // how the decommission confirmation works — it is a choice made about the
    // record in front of you, not a place you navigate to.
    const [provisionOpen, setProvisionOpen] = useState(false);

    // FNP-998: Decommission renders in the page-header toolbar with every other
    // action, per the platform convention. The hook needs the record (for its
    // eligibility check), the stage being torn down, and the handler.
    const {
        mode,
        tools,
    } = useEditPageTools(
        application,
        stage === 'ide' ? 'dev' : stage,
        () => {
            // The SAME stage the button used for its eligibility check is the
            // one torn down — read once so the check and the action can never
            // disagree about which environment is being destroyed.
            void deprovisionRecord(stage === 'ide' ? 'dev' : stage);
        },
        // FNP-1007: correct the record instead of tearing anything down.
        //
        // ⚠️ Takes NO stage, unlike the handler above. The server's sweep is
        // application-wide — stacks, buckets, repositories and pipelines across
        // every environment — so passing one would imply a narrowing that does
        // not happen.
        //
        // The result is RETURNED rather than swallowed: a refusal resolves
        // normally with `marked: false`, and the modal renders `found` and
        // `incompleteChecks` distinctly from it.
        () => markRecordDecommissioned(),
        // FNP-1013: open the provision dialog. The playbook and stage are
        // chosen there, not here — the whole point is that the caller supplies
        // the playbook id rather than the code deriving one.
        () => setProvisionOpen(true),
    );


    useEffect(() => {
        handleClearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect: deliberately runs once, adding deps would re-fire it
    }, []);


    const tabs = []
    if (allowedActions.includes(rbacActions.ListApplicationAccounts)) {
        tabs.push({
                      title: 'Accounts',
                      component: <ApplicationsAccountsPage/>
                  })
    }
    if (allowedActions.includes(rbacActions.ListApplicationProducts)) {
        tabs.push({
                      title: 'Products',
                      component: <ApplicationProductsPage/>
                  })
    }

    // FNP-998: repairing provisioning-resource pointers. Gated on
    // UpdateApplication because it writes to the record — the same permission
    // the main edit form requires.
    //
    // This is what unblocks decommissioning an application provisioned before
    // `infraStackNames` was persisted at create: the teardown refuses without a
    // stack name and will not guess one, so an admin fills it in here.
    if (allowedActions.includes(rbacActions.UpdateApplication) && application) {
        tabs.push({
                      title: 'Provisioning',
                      component: <ProvisioningMetadataForm
                          record={application}
                          handleSubmit={(values) => updateRecord({
                              ...values,
                              id: application.id,
                              // ⚠️ `name` is deliberately NOT sent. FNP-176
                              // rejects the field's PRESENCE, not a change to
                              // it — "semantic immutability, not equality" — so
                              // sending it unchanged is still refused. It was
                              // sent here originally because the schema
                              // declared it required; FNP-998 made it optional,
                              // which was the actual bug.
                          } as never)}
                          handleCancel={() => navigate(-1)}
                      />
                  })
    }

    // FNP-1016: the infrastructure INVENTORY — what resources exist, as a
    // record separate from the application itself.
    //
    // Distinct from the Provisioning tab above, which repairs the legacy
    // denormalised fields ON the Application. This tab reads and writes the
    // `ApplicationInfrastructure` entity, and is where an admin registers the
    // seven legacy production applications this platform never provisioned.
    //
    // Gated on UpdateApplication: registering writes an inventory record for
    // the application, so it needs the same permission as editing it.
    if (allowedActions.includes(rbacActions.UpdateApplication) && application) {
        tabs.push({
                      title: 'Infrastructure',
                      component: <ApplicationInfrastructureSection application={application}/>
                  })
    }


    if (!application) {
        return <PlatformLoader message='Loading application...Please wait'/>;
    }


    return (
        <FormPageLayout
            title={application.name ? application.name : application.id}
            pageNavigation={<EditPageNavigation component={application}/>}
            tools={tools}
        >

            {/*
              * FNP-1500: a drawer on the PlatformDrawer chain, showing the four
              * steps and every derived name before anything is built.
              *
              * ⚠️ The drawer closes ITSELF, after `onConfirm` resolves. It used
              * to be closed here in the same tick as the call, so a refusal
              * from the server destroyed the operator's input and toasted into
              * a dialog that was no longer on screen.
              */}
            <ProvisionFormDrawer
                opened={provisionOpen}
                onClose={() => setProvisionOpen(false)}
                provisioning={provisioning}
                record={application}
                onConfirm={async ({
                                     playbookId,
                                     stage: provisionStage,
                                     subdomain,
                                     deploymentAccountId,
                                     baseName,
                                     addressing,
                                 }) => {
                    // FNP-1542: `baseName` MUST be forwarded.
                    //
                    // ⚠️ IT USED TO BE DESTRUCTURED AWAY RIGHT HERE. The drawer
                    // collected it, showed every name derived from it, checked
                    // each against what exists — and then this handler dropped
                    // it on the floor. The orchestrator fell back to the
                    // application's `name`, so the panel validated names that
                    // were never created.
                    //
                    // That was survivable while the name was itself a slug.
                    // FNP-1537 freed the name to be human ("Flexi Testing"),
                    // and the fallback then produced a repository name
                    // CodeCommit refuses outright — so every provision failed.
                    // FNP-1568: `addressing` is forwarded for the same reason,
                    // and is the third field this statement has had to learn.
                    // The guard in fnp-1561-no-field-goes-nowhere.test.ts
                    // fails when a field the drawer submits is not named here.
                    await provisionRecord(
                        playbookId, provisionStage, subdomain, deploymentAccountId,
                        baseName, addressing,
                    );
                }}
            />

            {mode === 'view' ?

                <Stack gap={'xl'}>
                    <ApplicationProgressSection application={application}/>

                    <Grid>
                        <Grid.Col span={{
                            base: 12,
                            lg: 4
                        }}>
                            <Profile
                                record={application}
                            />
                        </Grid.Col>
                        <Grid.Col span={{
                            base: 12,
                            lg: 8
                        }}>
                            <TabbedComponent tabs={tabs}/>
                        </Grid.Col>
                    </Grid>

                </Stack>

                :
                <SimplePageContentWrapper>
                    <RecordForm
                        record={application}
                        handleSubmit={updateRecord}
                        handleCancel={() => navigate('/applications')}
                    />
                </SimplePageContentWrapper>
            }


        </FormPageLayout>
    );
};
